import { useFormContext, useWatch } from 'react-hook-form'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Alert, Grid, IconButton, Tooltip } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import InfoSection from '@/components/ui/sections/InfoSection'
import TextInputController from '@/components/ui/inputs/TextInputController'
import PhoneNumberInputController from '@/components/ui/inputs/PhoneNumberInputController'

import { PatientProfileFormData } from '../model/usePatientProfileForm'
import { Mode } from '../model/types'

export function PrivateDoctorSection({ editable, mode }: { editable: boolean; mode: Mode }) {
  useLingui()
  const rhForm = useFormContext<PatientProfileFormData>()
  const {
    formState: { defaultValues },
  } = rhForm
  const privateDoctor = useWatch({ control: rhForm.control, name: 'privateDoctor' })

  const showNoPrivateDoctor = mode === 'edit' && !editable && !defaultValues?.privateDoctor

  return (
    <InfoSection action={editable && <ActionButton />} label={t`Primary Care Physician`}>
      {showNoPrivateDoctor && (
        <Alert icon={false} severity="info" variant="outlined" sx={{ mt: '10px' }}>
          <Trans>This patient does not have a primary care physician</Trans>
        </Alert>
      )}
      {privateDoctor && (
        <Grid container spacing={3} sx={{ mt: '0px' }}>
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`First name`} name="privateDoctor.firstname" readOnly={!editable} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`Last name`} name="privateDoctor.lastname" readOnly={!editable} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`Email`} name="privateDoctor.email" readOnly={!editable} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneNumberInputController control={rhForm.control} label={t`Phone`} name="privateDoctor.phone" readOnly={!editable} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`City`} name="privateDoctor.city" readOnly={!editable} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`Postal code`} name="privateDoctor.cp" readOnly={!editable} />
          </Grid>
        </Grid>
      )}
    </InfoSection>
  )
}

function ActionButton() {
  const rhForm = useFormContext<PatientProfileFormData>()
  const privateDoctor = useWatch({ control: rhForm.control, name: 'privateDoctor' })

  if (privateDoctor) {
    return (
      <Tooltip title={t`Delete`}>
        <IconButton
          onClick={() => {
            rhForm.setValue('privateDoctor', undefined)
          }}
          size="small"
        >
          <DeleteRoundedIcon />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={t`Add`}>
      <IconButton
        onClick={() => {
          rhForm.setValue('privateDoctor', {
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            city: '',
            cp: '',
          })
        }}
        size="small"
      >
        <AddRoundedIcon />
      </IconButton>
    </Tooltip>
  )
}
